import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import { useState, useEffect, useCallback } from 'react';
import { useBuilderStore } from '../../stores/builderStore';
import { createField, fieldRegistry } from '../../lib/fieldRegistry';
import FieldPalette from './FieldPalette';
import FormCanvas from './FormCanvas';
import FieldConfigurator from './FieldConfigurator';
import FormPreview from './FormPreview';
import FormStyling from './FormStyling';
import PollSettings from './PollSettings';
import ApprovalSettings from './ApprovalSettings';
import CollaboratorSettings from './CollaboratorSettings';
import FormScheduleSettings from './FormScheduleSettings';
import SaveResumeSettings from './SaveResumeSettings';
import QuizSettingsModal from './QuizSettingsModal';
import WebhookSettings from './WebhookSettings';
import IntegrationsPanel from '../admin/IntegrationsPanel';
import VersionHistory from './VersionHistory';
import ConditionalLogicBuilder from './ConditionalLogicBuilder';
import { useThemeStore } from '../../stores/themeStore';
import type { FieldType } from '../../types/form';

interface BuilderProps {
  formId?: string;
}

export default function Builder({ formId }: BuilderProps) {
  const { addField, setDragging, selectedFieldId, removeField, duplicateField, formSchema } = useBuilderStore();
  useThemeStore(); // Keep store subscription for dark mode reactivity
  const { undo, redo } = useBuilderStore.temporal.getState();

  // Check if this is a quiz-type form (has quiz mode enabled)
  const isQuizForm = formSchema.settings.quiz?.enabled === true;
  const [activeType, setActiveType] = useState<FieldType | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isStylingOpen, setIsStylingOpen] = useState(false);
  const [isPollSettingsOpen, setIsPollSettingsOpen] = useState(false);
  const [isApprovalSettingsOpen, setIsApprovalSettingsOpen] = useState(false);
  const [isCollaboratorSettingsOpen, setIsCollaboratorSettingsOpen] = useState(false);
  const [isScheduleSettingsOpen, setIsScheduleSettingsOpen] = useState(false);
  const [isSaveResumeSettingsOpen, setIsSaveResumeSettingsOpen] = useState(false);
  const [isQuizSettingsOpen, setIsQuizSettingsOpen] = useState(false);
  const [isWebhookSettingsOpen, setIsWebhookSettingsOpen] = useState(false);
  const [isIntegrationsOpen, setIsIntegrationsOpen] = useState(false);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [isConditionalLogicOpen, setIsConditionalLogicOpen] = useState(false);
  const [showMobilePalette, setShowMobilePalette] = useState(false);
  const [showMobileConfig, setShowMobileConfig] = useState(false);

  // Subscribe to temporal store changes
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  useEffect(() => {
    const unsubscribe = useBuilderStore.temporal.subscribe((state) => {
      setCanUndo(state.pastStates.length > 0);
      setCanRedo(state.futureStates.length > 0);
    });
    return unsubscribe;
  }, []);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      return;
    }

    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      undo();
    }

    if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
      e.preventDefault();
      redo();
    }

    if ((e.key === 'Delete' || e.key === 'Backspace') && selectedFieldId) {
      e.preventDefault();
      removeField(selectedFieldId);
    }

    if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedFieldId) {
      e.preventDefault();
      duplicateField(selectedFieldId);
    }
  }, [selectedFieldId, removeField, duplicateField, undo, redo]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  function handleDragStart(event: DragStartEvent) {
    const { active } = event;
    setDragging(true);

    if (active.data.current?.isNew) {
      setActiveType(active.data.current.type as FieldType);
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveType(null);
    setDragging(false);

    if (!over) return;

    // Only handle adding new fields from palette
    if (active.data.current?.isNew && active.data.current?.type) {
      const fieldType = active.data.current.type as FieldType;
      const newField = createField(fieldType);

      if (over.id === 'canvas') {
        addField(newField);
      } else if (typeof over.id === 'string') {
        addField(newField, over.id);
      }
    }
  }

  function handleDragCancel() {
    setActiveType(null);
    setDragging(false);
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {/* Toolbar */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 border-r border-gray-200 dark:border-gray-700 pr-3 mr-3">
            <button
              onClick={() => undo()}
              disabled={!canUndo}
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Undo (Ctrl+Z)"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
            </button>
            <button
              onClick={() => redo()}
              disabled={!canRedo}
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Redo (Ctrl+Y)"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
              </svg>
            </button>
          </div>

          <div className="border-l border-gray-200 dark:border-gray-700 pl-3 ml-1">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium">Shortcuts:</span> Delete key to remove, Ctrl+D to duplicate
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsStylingOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-sm"
            title="Form Styling"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
            Styling
          </button>

          <button
            onClick={() => setIsSaveResumeSettingsOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-sm"
            title="Save & Resume Settings"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            Save
          </button>

          <button
            onClick={() => setIsWebhookSettingsOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-sm"
            title="Webhook Settings"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Webhook
          </button>

          <button
            onClick={() => setIsIntegrationsOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-sm"
            title="Third-Party Integrations"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
            </svg>
            Integrate
          </button>

          <button
            onClick={() => setIsVersionHistoryOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-sm"
            title="Version History"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            History
          </button>

          <button
            onClick={() => setIsConditionalLogicOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-sm"
            title="Conditional Logic"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
            </svg>
            Logic
          </button>

          {isQuizForm && (
            <button
              onClick={() => setIsQuizSettingsOpen(true)}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-sm"
              title="Quiz Settings"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Quiz Settings
            </button>
          )}

          <div className="w-px h-6 bg-gray-200 dark:bg-gray-700"></div>

          <button
            onClick={() => setIsPreviewOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Preview
          </button>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-4 py-2 flex justify-around z-40">
        <button
          onClick={() => { setShowMobilePalette(true); setShowMobileConfig(false); }}
          className={`flex flex-col items-center p-2 rounded-lg ${showMobilePalette ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/20' : 'text-gray-500 dark:text-gray-400'}`}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span className="text-xs mt-1">Add Field</span>
        </button>
        <button
          onClick={() => { setShowMobilePalette(false); setShowMobileConfig(false); }}
          className={`flex flex-col items-center p-2 rounded-lg ${!showMobilePalette && !showMobileConfig ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/20' : 'text-gray-500 dark:text-gray-400'}`}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
          </svg>
          <span className="text-xs mt-1">Canvas</span>
        </button>
        <button
          onClick={() => { setShowMobileConfig(true); setShowMobilePalette(false); }}
          className={`flex flex-col items-center p-2 rounded-lg ${showMobileConfig ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/20' : 'text-gray-500 dark:text-gray-400'}`}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-xs mt-1">Configure</span>
        </button>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:flex h-[calc(100vh-11rem)]">
        <FieldPalette />
        <FormCanvas />
        <FieldConfigurator />
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden h-[calc(100vh-14rem)] relative">
        {showMobilePalette && (
          <div className="absolute inset-0 z-30 bg-white dark:bg-gray-900 overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 dark:text-white">Add Field</h2>
              <button
                onClick={() => setShowMobilePalette(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <FieldPalette />
          </div>
        )}

        {showMobileConfig && (
          <div className="absolute inset-0 z-30 bg-white dark:bg-gray-900 overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 dark:text-white">Configure Field</h2>
              <button
                onClick={() => setShowMobileConfig(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <FieldConfigurator />
          </div>
        )}

        <div className={showMobilePalette || showMobileConfig ? 'hidden' : ''}>
          <FormCanvas />
        </div>
      </div>

      {/* Drag Overlay - only for new fields from palette */}
      <DragOverlay>
        {activeType && (
          <div className="bg-white dark:bg-gray-800 border-2 border-primary-500 rounded-lg p-3 shadow-xl w-48">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 flex items-center justify-center bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400 rounded text-base">
                {fieldRegistry[activeType].icon}
              </span>
              <span className="font-medium text-gray-900 dark:text-white text-sm">{fieldRegistry[activeType].label}</span>
            </div>
          </div>
        )}
      </DragOverlay>

      {/* Modals */}
      <FormPreview isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} />

      {formId && (
        <>
          <FormStyling formId={formId} isOpen={isStylingOpen} onClose={() => setIsStylingOpen(false)} />
          <PollSettings formId={formId} isOpen={isPollSettingsOpen} onClose={() => setIsPollSettingsOpen(false)} />
          <ApprovalSettings formId={formId} isOpen={isApprovalSettingsOpen} onClose={() => setIsApprovalSettingsOpen(false)} />
          <CollaboratorSettings formId={formId} isOpen={isCollaboratorSettingsOpen} onClose={() => setIsCollaboratorSettingsOpen(false)} />
          <FormScheduleSettings formId={formId} isOpen={isScheduleSettingsOpen} onClose={() => setIsScheduleSettingsOpen(false)} />
          <SaveResumeSettings formId={formId} isOpen={isSaveResumeSettingsOpen} onClose={() => setIsSaveResumeSettingsOpen(false)} />
          <QuizSettingsModal formId={formId} isOpen={isQuizSettingsOpen} onClose={() => setIsQuizSettingsOpen(false)} />
          <WebhookSettings formId={formId} isOpen={isWebhookSettingsOpen} onClose={() => setIsWebhookSettingsOpen(false)} />
          <VersionHistory formId={formId} isOpen={isVersionHistoryOpen} onClose={() => setIsVersionHistoryOpen(false)} />
        </>
      )}

      {formId && (
        <IntegrationsPanel formId={formId} isOpen={isIntegrationsOpen} onClose={() => setIsIntegrationsOpen(false)} />
      )}
      <ConditionalLogicBuilder isOpen={isConditionalLogicOpen} onClose={() => setIsConditionalLogicOpen(false)} />
    </DndContext>
  );
}
